import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from 'shared/components/UC/Button';
import SearchableSelect from 'shared/components/SearchableSelect';
import Toast from 'shared/components/Toast';
import adminService from '../services/adminService';
import { UserRole } from '../types/auth';
import {
  affiliationsData,
  facultadesData,
  unidadesData,
} from '../utils/affiliations';
import styles from './AdminUserDetail.module.css';

const AdminUserCreate: React.FC = () => {
  const navigate = useNavigate();

  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<{
    message: string;
    type: 'error' | 'success' | 'warning' | 'info';
  } | null>(null);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    role: 'researcher' as UserRole,
    isActive: true,
    run: '',
    affiliation: '',
    facultadUC: '',
    unidadAcademica: '',
    researcherType: '' as 'consolidated' | 'emerging' | '',
    doctorateYear: '',
    canApply: true,
    platformMessage: '',
  });

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => {
    const { name, value, type } = e.target;

    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData((prev) => ({ ...prev, [name]: checked }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate required fields
    if (!formData.name || !formData.email) {
      setToast({
        message: 'Nombre y email son campos requeridos',
        type: 'error',
      });
      return;
    }

    try {
      setSaving(true);

      const createDto = {
        name: formData.name,
        email: formData.email,
        role: formData.role,
        isActive: formData.isActive,
        profile: {
          run: formData.run || undefined,
          affiliation: formData.affiliation || undefined,
          facultadUC: formData.facultadUC || undefined,
          unidadAcademica: formData.unidadAcademica || undefined,
          researcherType: formData.researcherType || undefined,
          doctorateYear: formData.doctorateYear
            ? parseInt(formData.doctorateYear)
            : undefined,
          canApply: formData.canApply,
          platformMessage: formData.platformMessage || undefined,
        },
      };

      const createdUser = await adminService.createUser(createDto);

      setToast({
        message: 'Usuario creado exitosamente',
        type: 'success',
      });

      // Navigate to the edit page after a short delay
      setTimeout(() => {
        navigate(`/admin/users/${createdUser.id}`);
      }, 1500);
    } catch (err) {
      setToast({
        message: err instanceof Error ? err.message : 'Error al crear usuario',
        type: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate('/admin/users');
  };

  return (
    <div className={styles.pageWrapper}>
      <div className={styles.headerContainer}>
        <div className={styles.header}>
          <div className={styles.headerContent}>
            <h1 className={styles.title}>Crear Usuario</h1>
            <p className={styles.subtitle}>
              Crea un nuevo usuario en el sistema
            </p>
          </div>
        </div>
      </div>

      <div className={styles.container}>
        <div className={styles.content}>
          <form className={styles.form} onSubmit={handleSave}>
            {/* Basic Information Section */}
            <section className={styles.formSection}>
              <h2 className={styles.sectionTitle}>Información Básica</h2>

              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label htmlFor="name" className={styles.label}>
                    Nombre <span className={styles.required}>*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className={styles.input}
                    required
                  />
                </div>

                <div className={styles.formGroup}>
                  <label htmlFor="email" className={styles.label}>
                    Email <span className={styles.required}>*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className={styles.input}
                    required
                  />
                </div>
              </div>

              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label htmlFor="run" className={styles.label}>
                    RUN
                  </label>
                  <input
                    type="text"
                    id="run"
                    name="run"
                    value={formData.run}
                    onChange={handleInputChange}
                    className={styles.input}
                    placeholder="12.345.678-9"
                  />
                </div>

                <div className={styles.formGroup}>
                  <label htmlFor="role" className={styles.label}>
                    Rol <span className={styles.required}>*</span>
                  </label>
                  <select
                    id="role"
                    name="role"
                    value={formData.role}
                    onChange={handleInputChange}
                    className={styles.select}
                    required
                  >
                    <option value="researcher">Investigador</option>
                    <option value="admin">Administrador</option>
                    <option value="evaluator">Evaluador</option>
                  </select>
                </div>
              </div>

              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label className={styles.checkboxLabel}>
                    <input
                      type="checkbox"
                      name="canApply"
                      checked={formData.canApply}
                      onChange={handleInputChange}
                      className={styles.checkbox}
                    />
                    <span>Puede postular</span>
                  </label>
                </div>
              </div>
            </section>

            {/* Academic Information Section */}
            <section className={styles.formSection}>
              <h2 className={styles.sectionTitle}>Información Académica</h2>

              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <SearchableSelect
                    id="affiliation"
                    name="affiliation"
                    label="Acceso a"
                    options={affiliationsData}
                    value={formData.affiliation}
                    onChange={handleInputChange}
                    placeholder="Buscar o ingresar afiliación..."
                    allowManualEntry={true}
                  />
                </div>

                <div className={styles.formGroup}>
                  <SearchableSelect
                    id="facultadUC"
                    name="facultadUC"
                    label="Facultad UC"
                    options={facultadesData}
                    value={formData.facultadUC}
                    onChange={handleInputChange}
                    placeholder="Buscar o ingresar facultad..."
                    allowManualEntry={true}
                  />
                </div>
              </div>

              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <SearchableSelect
                    id="unidadAcademica"
                    name="unidadAcademica"
                    label="Unidad Académica"
                    options={unidadesData}
                    value={formData.unidadAcademica}
                    onChange={handleInputChange}
                    placeholder="Buscar o ingresar unidad académica..."
                    allowManualEntry={true}
                  />
                </div>

                <div className={styles.formGroup}>
                  <label htmlFor="researcherType" className={styles.label}>
                    Tipo de Investigador
                  </label>
                  <select
                    id="researcherType"
                    name="researcherType"
                    value={formData.researcherType}
                    onChange={handleInputChange}
                    className={styles.select}
                  >
                    <option value="">Seleccionar...</option>
                    <option value="consolidated">Consolidado</option>
                    <option value="emerging">Emergente</option>
                  </select>
                </div>
              </div>

              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label htmlFor="doctorateYear" className={styles.label}>
                    Año de Doctorado
                  </label>
                  <input
                    type="number"
                    id="doctorateYear"
                    name="doctorateYear"
                    value={formData.doctorateYear}
                    onChange={handleInputChange}
                    className={styles.input}
                    placeholder="2020"
                    min="1950"
                    max={new Date().getFullYear()}
                  />
                </div>
              </div>
            </section>

            {/* Platform Settings Section */}
            <section className={styles.formSection}>
              <h2 className={styles.sectionTitle}>
                Configuración de Plataforma
              </h2>

              <div className={styles.formGroup}>
                <label htmlFor="platformMessage" className={styles.label}>
                  Mensaje de error de la plataforma
                </label>
                <textarea
                  id="platformMessage"
                  name="platformMessage"
                  value={formData.platformMessage}
                  onChange={handleInputChange}
                  className={styles.textarea}
                  rows={4}
                  placeholder="Mensaje personalizado para mostrar al usuario en la plataforma"
                />
              </div>
            </section>

            {/* Form Actions */}
            <div className={styles.formActions}>
              <Button onClick={handleCancel} variant="secondary" type="button">
                Cancelar
              </Button>
              <Button variant="primary" type="submit" disabled={saving}>
                {saving ? 'Guardando...' : 'Crear Usuario'}
              </Button>
            </div>
          </form>
        </div>
      </div>

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
};

export default AdminUserCreate;
